import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export const exportLedgerToExcel = async (
  ledgerRows,
  selectedAccount,
  startDate,
  endDate,
  accountName,
  selectedType = ""
) => {
  if (!ledgerRows || ledgerRows.length === 0) return;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Comfort Line";
  wb.created = new Date();

  const ws = wb.addWorksheet("Ledger", {
    properties: { defaultRowHeight: 18 },
    views: [{ state: "frozen", ySplit: 4 }],
  });

  ws.mergeCells("A1:I1");
  ws.getCell("A1").value = selectedType ? "Ledger Report by Type" : "Ledger Report";
  ws.getCell("A1").font = { bold: true, size: 16 };
  ws.getCell("A1").alignment = { vertical: "middle", horizontal: "left" };

  ws.mergeCells("A2:I2");
  ws.getCell("A2").value = `Report: ${accountName || "Account"}`;
  ws.getCell("A2").font = { bold: true, size: 11 };

  ws.mergeCells("A3:I3");
  ws.getCell("A3").value = `Period: ${startDate} to ${endDate}${selectedType ? " | Type movement only" : ""}`;
  ws.getCell("A3").font = { size: 10 };

  const headerRow = ws.addRow([
    "Date",
    "Account",
    "Type",
    "Description",
    "Amount In",
    "Amount Out",
    "Running Balance",
    "Paid By",
    "Comments",
  ]);

  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E4DB7" } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
  });

  ws.columns = [
    { key: "date", width: 13 },
    { key: "account", width: 28 },
    { key: "type", width: 20 },
    { key: "desc", width: 48 },
    { key: "in", width: 14 },
    { key: "out", width: 14 },
    { key: "bal", width: 18 },
    { key: "paidBy", width: 20 },
    { key: "comment", width: 28 },
  ];

  const moneyFmt = "#,##0.00";

  ledgerRows.forEach((r) => {
    const row = ws.addRow([
      r.isBFRow ? "-" : r.formattedDate || "-",
      r.accountName || "-",
      r.expenseType || "-",
      r.description || "-",
      r.amountIn ? Number(r.amountIn) : null,
      r.amountOut ? Number(r.amountOut) : null,
      typeof r.runningBalance === "number" ? Number(r.runningBalance) : null,
      r.paidBy || "-",
      r.comment || "-",
    ]);

    row.eachCell((cell, colNumber) => {
      cell.alignment = {
        vertical: "middle",
        horizontal: colNumber >= 5 && colNumber <= 7 ? "right" : "left",
        wrapText: colNumber === 4 || colNumber === 9,
      };
      cell.border = {
        top: { style: "thin", color: { argb: "FFBFBFBF" } },
        left: { style: "thin", color: { argb: "FFBFBFBF" } },
        bottom: { style: "thin", color: { argb: "FFBFBFBF" } },
        right: { style: "thin", color: { argb: "FFBFBFBF" } },
      };
    });

    [5, 6, 7].forEach((idx) => {
      const c = row.getCell(idx);
      if (typeof c.value === "number") c.numFmt = moneyFmt;
    });

    if (r.isBFRow) {
      row.font = { bold: true };
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFAFAFA" } };
    } else if ((r.expenseType || "") === "Auto Mirror") {
      row.font = { color: { argb: "FF777777" } };
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF9F9F9" } };
    }
  });

  const dataRows = ledgerRows.filter((r) => !r.isBFRow);
  const totalIn = dataRows.reduce((a, b) => a + Number(b.amountIn || 0), 0);
  const totalOut = dataRows.reduce((a, b) => a + Number(b.amountOut || 0), 0);
  const closingBalance = dataRows.length > 0
    ? Number(dataRows[dataRows.length - 1].runningBalance || 0)
    : Number(ledgerRows.find((r) => r.isBFRow)?.runningBalance || 0);

  ws.addRow([]);
  const sum1 = ws.addRow(["", "", "", "Total In", totalIn]);
  const sum2 = ws.addRow(["", "", "", "Total Out", totalOut]);
  const sum3 = ws.addRow(["", "", "", "Closing Balance", closingBalance]);

  [sum1, sum2, sum3].forEach((r) => {
    r.getCell(4).font = { bold: true };
    r.getCell(5).numFmt = moneyFmt;
    r.getCell(5).alignment = { horizontal: "right" };
  });

  const safeFileName = `Ledger_${accountName || "Account"}_${selectedType || "AllTypes"}_${startDate}_to_${endDate}.xlsx`
    .replace(/[<>:"/\\|?*]+/g, "")
    .trim();

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), safeFileName);
};
